<template>
  <article class="recap" :class="{
      'recap--completely': showAdvices && isRwSatisfiedCompletely,
      'recap--only-calculated': showAdvices && !isRwSatisfiedCompletely && isRwSatisfied,
      'recap--advices': showAdvices,
      'recap--advices-in-result': isResultPage && showAdvices,
      'recap--plaster': isPlaster && !isResultPage,
    }">
    <p class="recap__text">{{ showAdvices ? conclusion : recap }}</p>
    <p class="recap__formula" v-if="isResultPage">
      <span v-if="hasRw" class="recap__air-indexes">
        R<sub>{{ $t(`message.constructionParams.indexes.calculated.subs.${isRwTested ? 'rw-tested' : 'rw'}`) }}</sub>
        <span v-if="isRwEqual"> = </span>
        <span v-else>{{ isRwSatisfied ? ' ≥ ' : ' ≤ '}}</span>
        R<sub>{{ $t('message.constructionParams.indexes.subs.rw') }}</sub>
      </span>
      <span v-if="isFloor" class="recap__shock-indexes">
        L<sub>{{ $t('message.constructionParams.indexes.calculated.subs.lnw') }}</sub>
        <span>{{ isLnwSatisfied ? ' ≤ ' : ' ≥ ' }}</span>
        L<sub>{{ $t('message.constructionParams.indexes.subs.lnw') }}</sub>
      </span>
      <span v-if="hasRw && isRwSatisfiedCompletely" class="recap__down-indexes">
        R<sub>{{ $t(`message.constructionParams.indexes.rwDown.subs.rw`) }}</sub>
        <span v-if="isRwSatisfiedCompletelyEqual"> = </span>
        <span v-else> ≥ </span>
        R<sub>{{ $t(`message.constructionParams.indexes.subs.rw`) }}</sub>
      </span>
    </p>
  </article>
</template>

<script>
import IconMixin from '@/components/mixin/IconMixin'
import { CalcTypes, ConstructionTypes } from '@/utils/types'

export default {
  name: 'Recap',
  props: ['indexes', 'sector', 'isResultPage'],
  mixins: [IconMixin],
  computed: {
    showAdvices() {
      const isWallOrCeilingPRO = [ConstructionTypes.wall, ConstructionTypes.ceiling]
        .includes(this.sector?.constructionType?.type) && this.sector?.algorithm === CalcTypes.pro
      const isWithSize = this.sector?.system?.isSizeable
      const isWithPlaster = this.sector?.system?.isPlaster

      if (isWithPlaster) {
        return isWallOrCeilingPRO && isWithPlaster && this.isRwDownNotSatisfied
      }

      return isWallOrCeilingPRO && isWithSize && (this.isRwSatisfiedCompletely || this.isRwSatisfied)
    },
    isRwTested() {
      return this.indexes.isTested
    },
    isFloor() {
      return this.indexes.required.lnw !== null
    },
    isPlaster() {
      return this.sector?.system?.isPlaster
    },
    isRwSatisfied() {
      return this.indexes.calculated.rw >= this.indexes.required.rw
    },
    isRwDownNotSatisfied() {
      return this.isRwSatisfied && (this.indexes.rwDown && this.indexes.rwDown <= this.indexes?.required.rw)
    },
    isRwEqual() {
      return this.indexes.calculated.rw === this.indexes.required.rw
    },
    isRwSatisfiedCompletely() {
      return this.isRwSatisfied &&
        (this.indexes.rwDown && this.indexes.rwDown >= this.indexes?.required.rw)
    },
    isRwSatisfiedCompletelyEqual() {
      return this.indexes.rwDown === this.indexes.required.rw
    },
    hasRw() {
      return this.indexes.calculated && this.indexes.calculated.rw !== null && this.indexes.required.rw !== null
    },
    isLnwSatisfied() {
      return this.indexes.required.lnw >= this.indexes.calculated.lnw
    },
    recap() {
      const isSatisfied = this.isFloor ? (this.isRwSatisfied && this.isLnwSatisfied) : this.isRwSatisfied
      let constructionType = this.isFloor ? 'floors' : 'walls'

      if (this.isFloor && !this.hasRw) {
        constructionType = constructionType + 'WithoutRw'
      }

      return isSatisfied
        ? this.$t(`message.result.recap.${constructionType}.satisfied`)
        : this.$t(`message.result.recap.${constructionType}.noSatisfied`)
    },
    conclusion() {
      const calc = this.isPlaster ? 'calc-plaster' : 'calc'

      return this.$t(`message.result.recap.conclusions.rw-satisfied-${
        this.isRwSatisfiedCompletely ? 'completely' : calc
      }`)
    }
  }
}
</script>

<style scoped lang="sass">
.recap
  display: grid
  grid-template-columns: auto auto
  +media((grid-template-areas: (0: 'text text' 'formula formula', 620: 'text formula' 'text formula')))
  padding: rem(20)
  margin: 0 0 rem(30)
  border-radius: $block-radius
  background: $success
  text-align: left
  &__text
    grid-area: text
    margin: rem(7) auto rem(7) 0
    +media((margin: (0: rem(7) 0 rem(7) 0, 620: rem(7) rem(10) rem(7) 0)))
  &__formula
    grid-area: formula
    display: flex
    flex-shrink: 0
    align-self: center
    gap: rem(8)
    @extend %18
    line-height: 1.3
    +media((flex-direction: (0: row, 620: column)))
    +media((justify-content: (0: space-around, 620: flex-start)))
    +media((margin: (0: rem(10) 0 0 0, 620: 0 0 0 auto)))
  &--completely
    background: $warning-success
  &--only-calculated
    background: $warning-info
  &--advices
    width: 100%
    +media((grid-template-areas: (0: 'text text' 'text text', 620: 'text text' 'text text')))
  &--advices-in-result
    +media((grid-template-areas: (0: 'text text' 'formula formula', 620: 'text formula' 'text formula')))
  &--plaster
    width: 100%
    +media((grid-template-areas: (0: 'text text' 'text text', 620: 'text text' 'text text')))
</style>
